import {
  Button,
  Checkbox,
  Message,
  Table,
  Tag,
  TagPicker,
  useToaster,
} from 'rsuite';
import React, { Dispatch, SetStateAction, useEffect } from 'react';

import * as requestsApi from '../../../api/requests';
import * as coursesApi from '../../../api/courses';
import DeleteIcon from '@rsuite/icons/Trash';
import CheckIcon from '@rsuite/icons/Check';
import { FormModal } from '../../../common/FormModal';
import RequestsAndStudentsSearcher from '../../../common/RequestsAndStudentsSearcher';
import { useConfirm } from '../../../common/useConfirm';
import {
  renderTableEmpty,
  renderTableLoading,
} from '../../../common/TableStates';

type Props = {
  courseId: string;
  setCourseId: Dispatch<SetStateAction<string>>;
  callback: () => void;
};

const { Column, HeaderCell, Cell } = Table;

export default function CourseQueueModal({
  courseId,
  setCourseId,
  callback,
}: Props) {
  let cellClicked = false;
  const toaster = useToaster();
  const [confirm, ConfirmDialog] = useConfirm();
  const [initialRequests, setInitialRequests] = React.useState<any[]>([]);
  const [studentsList, setStudentsList] = React.useState<
    {
      id: any;
      email: any;
      firstName: string;
      secondName: string;
    }[]
  >([]);
  const [requestsList, setRequestsList] = React.useState<
    {
      id: any;
      email: any;
      firstName: string;
      secondName: string;
    }[]
  >([]);
  const [queueLoading, setQueueLoading] = React.useState<boolean>(false);
  const [addToQueueToggle, setAddToQueueToggle] =
    React.useState<boolean>(false);

  const [selectedRows, setSelectedRows] = React.useState<string[]>([]);
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('asc');

  const [queue, setQueue] = React.useState<any[]>([]);

  const deleteQueueEntries = async (studentIds: string[]) => {
    try {
      await coursesApi.removeFromQueue(courseId, studentIds);
      toaster.push(
        <Message type="success" showIcon closable>
          Студент{studentIds.length > 1 ? 'ів' : 'а'} видалено з черги
        </Message>,
        {
          placement: 'topEnd',
          duration: 1500,
        }
      );
      getQueue().catch(console.error);
    } catch (err: any) {
      console.error(err);
      const errorMessage =
        err.response?.data?.message ||
        'Не вдалося оновити чергу. Спробуйте пізніше';
      toaster.push(
        <Message type="error" showIcon closable>
          {errorMessage}
        </Message>,
        {
          placement: 'topEnd',
          duration: 1500,
        }
      );
    }
  };

  const addQueueEntries = async (
    requestIds: string[],
    studentIds: string[]
  ) => {
    try {
      await coursesApi.addToQueue(courseId, requestIds, studentIds);
      toaster.push(
        <Message type="success" showIcon closable>
          Чергу оновлено
        </Message>,
        {
          placement: 'topEnd',
          duration: 1500,
        }
      );
      setRequestsList([]);
      getQueue().catch(console.error);
    } catch (err: any) {
      console.error(err);
      const errorMessage =
        err.response?.data?.message ||
        'Не вдалося додати студентів. Спробуйте пізніше';
      toaster.push(
        <Message type="error" showIcon closable>
          {errorMessage}
        </Message>,
        {
          placement: 'topEnd',
          duration: 1500,
        }
      );
    }
  };
  const getRequests = async (searchKeyword?: string) => {
    const requests = await requestsApi.list(
      1,
      20,
      searchKeyword,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined
    );
    setInitialRequests(
      requests.data.filter(
        (r: any) => !queue.find((q: any) => q.requestId === r.id)
      )
    );
  };
  const getQueue = async () => {
    setQueueLoading(true);
    const students = await coursesApi
      .getQueue(courseId, 'createdAt', sortOrder.toUpperCase())
      .catch(console.error);
    if (students) {
      setQueue(students.queue);
      // setFoundRequests(foundRequests.filter(r => !students.queue.find((q: any) => q.requestId === r.id)));
    }
    setQueueLoading(false);
  };

  useEffect(() => {
    if (courseId) {
      getQueue()
        .catch(console.error)
        .then(() => getRequests().catch(console.error));
    }
  }, [courseId]);

  useEffect(() => {
    if (queue && queue.length && initialRequests.length) {
      setInitialRequests(
        initialRequests.filter(r => !queue.find(s => s.requestId === r.id))
      );
    }
  }, [queue, initialRequests]);

  const selection = ({ id }: { id: string }) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter(r => r !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  return (
    <>
      {ConfirmDialog}
      <FormModal
        open={courseId.length > 0}
        onClose={() => setCourseId('')}
        title="Черга курсу"
        footer={
          <>
            <Button
              onClick={async () => {
                if (requestsList.length || studentsList.length) {
                  await addQueueEntries(
                    requestsList.map(r => r.id),
                    studentsList.map(s => s.id)
                  );
                }

                setCourseId('');
                setRequestsList([]);
                setStudentsList([]);
                setSelectedRows([]);
                callback();
              }}
              appearance="primary"
            >
              Додати
            </Button>
            <Button onClick={() => setCourseId('')} appearance="subtle">
              Скасувати
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex' }}>
          <div style={{ flexGrow: 1 }}>
            <Button
              onClick={() => {
                setAddToQueueToggle(!addToQueueToggle);
              }}
            >
              Додати в чергу
            </Button>
            {addToQueueToggle && (
              <div style={{ display: 'flex' }}>
                <div>
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <RequestsAndStudentsSearcher
                      requestsList={requestsList}
                      studentsList={studentsList}
                      setRequestsList={setRequestsList}
                      setStudentsList={setStudentsList}
                      initialRequestsList={initialRequests}
                      requestsFilter={(requests: any[]) =>
                        requests.filter(
                          r => !queue.find(qS => qS.requestId === r.id)
                        )
                      }
                      studentsFilter={(students: any[]) =>
                        students.filter(s => !queue.find(qS => qS.id === s.id))
                      }
                    />
                    <Button
                      style={{
                        width: 32,
                        height: 32,
                        alignSelf: 'end',
                        margin: 10,
                      }}
                      onClick={async () => {
                        await addQueueEntries(
                          requestsList.map(r => r.id),
                          studentsList.map(s => s.id)
                        );
                        setStudentsList([]);
                        setRequestsList([]);
                      }}
                    >
                      <CheckIcon />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div style={{ marginLeft: 'auto' }}>
            {selectedRows.length > 0 && (
              <>
                <Button
                  onClick={async () => {
                    const result = await confirm({
                      message: `Ви впевнені, що хочете видалити з черги ${selectedRows.length} студентів?`,
                      destructive: true,
                      confirmText: 'Видалити',
                    });
                    if (!result) {
                      return;
                    }
                    await deleteQueueEntries(selectedRows);
                  }}
                >
                  Прибрати з черги
                </Button>
              </>
            )}
          </div>
        </div>
        <Table
          loading={queueLoading}
          autoHeight={true}
          renderLoading={renderTableLoading}
          renderEmpty={renderTableEmpty}
          onRowClick={rowData => {
            if (cellClicked) {
              cellClicked = false;
              return;
            }
            selection({ id: rowData.id });
          }}
          sortColumn={'createdAt'}
          sortType={sortOrder}
          onSortColumn={(sortColumn, sortType) => {
            setSortOrder(sortType || 'asc');
            getQueue().catch(console.error);
          }}
          data={queue}
        >
          <Column width={64} resizable={true}>
            <HeaderCell>
              <Button
                appearance="link"
                onClick={() => {
                  if (selectedRows.length === queue.length) {
                    setSelectedRows([]);
                  } else {
                    setSelectedRows(queue.map(q => q.id));
                  }
                }}
              >
                <CheckIcon />
              </Button>
            </HeaderCell>
            <Cell style={{ display: 'flex', alignItems: 'center' }}>
              {rowData => (
                <Checkbox
                  checked={selectedRows.includes(rowData.id)}
                  onCheckboxClick={() => {
                    selection({ id: rowData.id });
                  }}
                />
              )}
            </Cell>
          </Column>
          <Column width={200} resizable={true}>
            <HeaderCell>Пошта</HeaderCell>
            <Cell dataKey="email" />
          </Column>
          <Column width={120} resizable={true}>
            <HeaderCell>Тип</HeaderCell>
            <Cell dataKey="typeName" />
          </Column>
          <Column width={180} resizable={true}>
            <HeaderCell>Ім'я</HeaderCell>
            <Cell dataKey="firstName" />
          </Column>
          <Column width={180} resizable={true}>
            <HeaderCell>Прізвище</HeaderCell>
            <Cell dataKey="secondName" />
          </Column>
          <Column width={180} sortable={true} resizable={true}>
            <HeaderCell>Очікує з</HeaderCell>
            <Cell dataKey="createdAt">
              {rowData =>
                new Date(rowData.queueEntryCreatedAt).toLocaleString()
              }
            </Cell>
          </Column>
          <Column width={80} resizable={true}>
            <HeaderCell>Дії</HeaderCell>
            <Cell>
              {rowData => (
                <>
                  <Button
                    appearance="subtle"
                    onClick={async () => {
                      cellClicked = true;
                      const result = await confirm({
                        message: `Ви впевнені, що хочете видалити з черги ${rowData.email} (${rowData.firstName} ${rowData.secondName})?`,
                        destructive: true,
                        confirmText: 'Видалити',
                      });
                      if (!result) {
                        return;
                      }
                      await deleteQueueEntries([rowData.id]);
                    }}
                  >
                    <DeleteIcon />
                  </Button>
                </>
              )}
            </Cell>
          </Column>
        </Table>
        {selectedRows.length ? <p>Виділено: {selectedRows.length}</p> : <></>}
      </FormModal>
    </>
  );
}
